import { useState, useEffect, useRef, useCallback } from 'react';
import { AdaptiveAudioManager } from './AdaptiveAudioManager';
import { FlexibleSecureContextManager } from './FlexibleSecureContextManager';
import { useVoiceAgent } from '../contexts/VoiceAgentContext';

// Simple icon components
const Mic = () => <span>🎤</span>;
const MicOff = () => <span>🔇</span>;
const Settings = () => <span>⚙️</span>;
const Eye = () => <span>👁️</span>;
const EyeOff = () => <span>🙈</span>;
const Minimize = () => <span>➖</span>;
const Chat = () => <span>💬</span>;

interface FloatingAvatarProps {
  onCommand?: (command: any) => void;
  onResult?: (result: any) => void;
  onError?: (error: any) => void;
}

interface ConversationContext {
  userIntent: string;
  currentTopic: string;
  previousCommands: string[];
  userPreferences: Record<string, any>;
  sessionHistory: Array<{
    timestamp: string;
    userInput: string;
    aiResponse: string;
    intent: string;
    emotion: string;
    confidence: number;
  }>;
  userEmotion: string;
  conversationFlow: 'listening' | 'processing' | 'speaking' | 'thinking';
  lastInteractionTime: number;
  userEngagement: number;
}

interface VoiceState {
  isListening: boolean;
  isSpeaking: boolean;
  isProcessing: boolean;
  audioLevel: number;
  confidence: number;
  language: string;
  accent: string;
  isContinuousListening: boolean;
}

const FloatingAvatar = ({ 
  onResult, 
  onError
}: FloatingAvatarProps) => {
  const { isVoiceAgentEnabled, toggleVoiceAgent } = useVoiceAgent();
  const [isVisible, setIsVisible] = useState(true);
  const [isMinimized, setIsMinimized] = useState(true); // Start collapsed, can expand to chat
  const [status, setStatus] = useState<'ready' | 'listening' | 'processing' | 'speaking' | 'thinking' | 'error' | 'permission-denied' | 'no-microphone' | 'not-supported'>('ready');
  const [commandHistory, setCommandHistory] = useState<Array<{id: number, command: string, response: string, timestamp: string}>>([]);
  const [currentCommand, setCurrentCommand] = useState('');
  const [isConversationLocked, setIsConversationLocked] = useState(false);
  const [isSessionActivated, setIsSessionActivated] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  // Avatar is locked in bottom right corner - no dragging needed
  const [voiceState, setVoiceState] = useState<VoiceState>({
    isListening: false,
    isSpeaking: false,
    isProcessing: false,
    audioLevel: 0,
    confidence: 0,
    language: 'en-US',
    accent: 'standard',
    isContinuousListening: true
  });
  
  const [conversationContext, setConversationContext] = useState<ConversationContext>({
    userIntent: '',
    currentTopic: 'general',
    previousCommands: [],
    userPreferences: {},
    sessionHistory: [],
    userEmotion: 'neutral',
    conversationFlow: 'listening',
    lastInteractionTime: Date.now(),
    userEngagement: 0.5
  });
  
  const containerRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<any>(null);
  const synthesisRef = useRef<SpeechSynthesis | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  // const analyserRef = useRef<AnalyserNode | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const processingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const conversationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const vadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const silenceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Adaptive Audio Manager
  const audioManagerRef = useRef<AdaptiveAudioManager | null>(null);

  // Initialize robust audio system
  useEffect(() => {
    if (typeof window !== 'undefined' && isVoiceAgentEnabled && isSessionActivated) {
      initializeRobustAudioSystem();
    }

    return () => {
      cleanupVoiceSystem();
    };
  }, [isVoiceAgentEnabled]);

  // Stop voice functionality when voice agent is disabled
  useEffect(() => {
    console.log('Voice agent state changed:', isVoiceAgentEnabled);
    
    if (!isVoiceAgentEnabled) {
      console.log('Voice agent disabled, stopping all voice functionality');
      
      // Stop continuous listening
      if (recognitionRef.current) {
        console.log('Stopping speech recognition');
        recognitionRef.current.stop();
      }
      
      // Stop speech synthesis
      if (synthesisRef.current) {
        console.log('Canceling speech synthesis');
        synthesisRef.current.cancel();
      }
      
      // Stop audio analysis
      if (audioContextRef.current) {
        console.log('Closing audio context');
        audioContextRef.current.close();
      }
      
      // Update voice state
      setVoiceState(prev => {
        console.log('Updating voice state to disabled');
        return {
          ...prev,
          isListening: false,
          isSpeaking: false,
          isProcessing: false,
          audioLevel: 0
        };
      });
      
      // Clear any timeouts
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
      if (conversationTimeoutRef.current) {
        clearTimeout(conversationTimeoutRef.current);
      }
      if (vadTimeoutRef.current) {
        clearTimeout(vadTimeoutRef.current);
      }
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
      }
    } else if (isVoiceAgentEnabled && isSessionActivated) {
      console.log('Voice agent enabled and session activated, initializing voice system');
      // Re-initialize voice system when enabled
      if (typeof window !== 'undefined') {
        initializeRobustAudioSystem();
      }
    }
  }, [isVoiceAgentEnabled, isSessionActivated]);

  const initializeRobustAudioSystem = async () => {
    try {
      // Check voice capabilities first
      const capabilities = FlexibleSecureContextManager.checkVoiceAvailability();
      console.log('Voice capabilities:', capabilities);
      
      const audioManager = new AdaptiveAudioManager();
      audioManagerRef.current = audioManager;
      
      // Set up callbacks
      audioManager.setCallbacks((status: string) => {
        setStatus(status as 'ready' | 'listening' | 'processing' | 'speaking' | 'thinking' | 'error' | 'permission-denied' | 'no-microphone' | 'not-supported');
        console.log('Avatar status:', status);
      });
      
      // Initialize the adaptive audio system
      await audioManager.initialize();
      
      // Start listening if voice is available
      if (capabilities.canUseVoice) {
        await audioManager.startListening();
      }
      
    } catch (error) {
      console.error('Failed to initialize adaptive audio system:', error);
      setStatus('ready');
    }
  };

  // const initializeVoiceSystem = async () => {
    try {
      // Check if we're in a secure context - allow localhost and HTTPS
      const isSecureContext = window.isSecureContext || 
                             window.location.hostname === 'localhost' || 
                             window.location.hostname === '127.0.0.1';
      
      if (!isSecureContext) {
        console.error('Speech recognition requires a secure context (HTTPS or localhost)');
        setStatus('not-supported');
        return;
      }

      // Initialize Web Speech API with enhanced settings for continuous listening
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      if (!SpeechRecognition) {
        console.error('Speech recognition not supported in this browser');
        setStatus('not-supported');
        return;
      }

      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true; // Enable continuous listening
      recognitionRef.current.interimResults = true; // Get interim results for real-time feedback
      recognitionRef.current.lang = voiceState.language;
      recognitionRef.current.maxAlternatives = 3; // Get multiple recognition alternatives
      
      // Enhanced event handlers for continuous listening
      recognitionRef.current.onstart = () => {
        setVoiceState(prev => ({ ...prev, isListening: true }));
        setStatus('listening');
        console.log('Continuous voice recognition started');
      };
      
      recognitionRef.current.onresult = (event: any) => {
        handleSpeechRecognitionResult(event);
      };
      
      recognitionRef.current.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error);
        handleVoiceError(event.error);
      };
      
              recognitionRef.current.onend = () => {
          setVoiceState(prev => ({ ...prev, isListening: false }));
          setStatus('ready');
          // Only restart if not processing and still enabled
          if (isVoiceAgentEnabled && voiceState.isContinuousListening && !voiceState.isProcessing) {
            setTimeout(() => {
              if (isVoiceAgentEnabled && voiceState.isContinuousListening && !voiceState.isProcessing) {
                startContinuousListening();
              }
            }, 1000); // Increased delay to prevent rapid restarts
          }
        };
      
      // Initialize speech synthesis with enhanced settings
      synthesisRef.current = window.speechSynthesis;
      
      // Get available voices and set optimal voice
      const voices = synthesisRef.current.getVoices();
      const preferredVoice = voices.find(voice => 
        voice.lang.includes('en') && voice.name.includes('Natural')
      ) || voices[0];
      
      if (preferredVoice) {
        console.log('Using voice:', preferredVoice.name);
      }
      
    } catch (error) {
      console.error('Failed to initialize voice system:', error);
      onError?.(error);
    }
  // };

  // const setupAudioAnalysis = async () => {
  //   try {
  //     // Check if mediaDevices is available
  //     if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
  //       console.error('MediaDevices API not supported');
  //       setStatus('error');
  //       return;
  //     }

  //     // Force permission request with explicit user interaction
  //     console.log('Requesting microphone permission...');
  //     const stream = await navigator.mediaDevices.getUserMedia({ 
  //       audio: {
  //         echoCancellation: true,
  //         noiseSuppression: true,
  //         autoGainControl: true,
  //         sampleRate: 44100
  //       } 
  //     });
      
  //     mediaStreamRef.current = stream;
  //     audioContextRef.current = new AudioContext();
  //     const source = audioContextRef.current.createMediaStreamSource(stream);
  //     analyserRef.current = audioContextRef.current.createAnalyser();
      
  //     analyserRef.current.fftSize = 256;
  //     const bufferLength = analyserRef.current.frequencyBinCount;
  //     const dataArray = new Uint8Array(bufferLength);
      
  //     source.connect(analyserRef.current);
      
  //     // Monitor audio levels for voice activity detection
  //     const updateAudioLevel = () => {
  //       if (analyserRef.current && isVoiceAgentEnabled) {
  //         analyserRef.current.getByteFrequencyData(dataArray);
  //         const average = dataArray.reduce((a, b) => a + b) / bufferLength;
  //         const normalizedLevel = average / 255;
          
  //         setVoiceState(prev => ({ 
  //           ...prev, 
  //           audioLevel: normalizedLevel 
  //         }));
          
  //         // Voice Activity Detection (VAD)
  //         if (normalizedLevel > 0.15) { // Threshold for voice detection
  //           setStatus('listening');
  //           // Clear silence timeout when voice is detected
  //           if (silenceTimeoutRef.current) {
  //             clearTimeout(silenceTimeoutRef.current);
  //           }
  //         } else if (normalizedLevel < 0.05) { // Silence detected
  //           // Start silence timeout
  //           if (!silenceTimeoutRef.current) {
  //             silenceTimeoutRef.current = setTimeout(() => {
  //               setStatus('ready');
  //             }, 2000); // Wait 2 seconds of silence before going to ready
  //           }
  //         }
  //       }
  //       requestAnimationFrame(updateAudioLevel);
  //     };
      
  //     updateAudioLevel();
      
  //   } catch (error: any) {
  //     console.error('Failed to setup audio analysis:', error);
      
  //     // Handle specific permission errors
  //     if (error.name === 'NotAllowedError') {
  //       console.error('Microphone permission denied. Please allow microphone access.');
  //       setStatus('permission-denied');
  //     } else if (error.name === 'NotFoundError') {
  //       console.error('No microphone found.');
  //       setStatus('no-microphone');
  //     } else if (error.name === 'NotSupportedError') {
  //       console.error('Audio not supported in this browser.');
  //       setStatus('not-supported');
  //     } else {
  //       setStatus('error');
  //     }
  //   }
  // };

  const startContinuousListening = () => {
    if (recognitionRef.current && isVoiceAgentEnabled && voiceState.isContinuousListening && !voiceState.isProcessing) {
      try {
        recognitionRef.current.start();
        console.log('Started continuous listening');
      } catch (error) {
        console.error('Failed to start continuous listening:', error);
        // Retry after a short delay
        setTimeout(() => {
          if (isVoiceAgentEnabled && voiceState.isContinuousListening && !voiceState.isProcessing) {
            startContinuousListening();
          }
        }, 1000);
      }
    }
  };

  const handleSpeechRecognitionResult = (event: any) => {
    // Don't process if conversation is locked or voice agent is disabled
    if (isConversationLocked || !isVoiceAgentEnabled) return;
    
    let finalTranscript = '';
    let interimTranscript = '';
    let maxConfidence = 0;

    for (let i = event.resultIndex; i < event.results.length; i++) {
      const transcript = event.results[i][0].transcript;
      const confidence = event.results[i][0].confidence;
      
      if (event.results[i].isFinal) {
        finalTranscript += transcript;
        maxConfidence = Math.max(maxConfidence, confidence);
      } else {
        interimTranscript += transcript;
      }
    }

    // Update confidence level
    setVoiceState(prev => ({ ...prev, confidence: maxConfidence }));

    if (finalTranscript) {
      processUserInput(finalTranscript, maxConfidence);
    } else if (interimTranscript) {
      // Show interim results for real-time feedback
      setCurrentCommand(interimTranscript);
    }
  };

  const handleVoiceError = (error: string) => {
    setVoiceState(prev => ({ 
      ...prev, 
      isListening: false, 
      isProcessing: false 
    }));
    
    // Provide user-friendly error messages
    let errorMessage = "I'm having trouble hearing you. ";
    let shouldSpeak = true;
    
    switch (error) {
      case 'no-speech':
        errorMessage += "Please speak clearly.";
        setStatus('ready');
        break;
      case 'audio-capture':
        errorMessage += "Please check your microphone.";
        setStatus('no-microphone');
        break;
      case 'network':
        errorMessage += "Network connection issue.";
        setStatus('error');
        break;
      case 'not-allowed':
        errorMessage = "I need microphone permission to hear you. Please click the microphone icon in your browser's address bar and allow access.";
        setStatus('permission-denied');
        shouldSpeak = false; // Don't speak this message as it's about microphone access
        break;
      case 'aborted':
        errorMessage += "Voice recognition was interrupted.";
        setStatus('ready');
        break;
      default:
        errorMessage += "Please try again.";
        setStatus('error');
    }
    
    if (shouldSpeak) {
      speakResponseWithOpenAI(errorMessage);
    }
    onError?.(error);
  };

  // const startConversationMonitoring = () => {
    // Monitor conversation flow and engagement
    conversationTimeoutRef.current = setInterval(() => {
      const timeSinceLastInteraction = Date.now() - conversationContext.lastInteractionTime;
      
      if (timeSinceLastInteraction > 30000 && conversationContext.userEngagement > 0.3) {
        // Proactive engagement
        const proactiveResponse = generateProactiveResponse();
        speakResponseWithOpenAI(proactiveResponse);
      }
    }, 10000);
  // };

  // IMMEDIATE RESPONSIVE CONVERSATION SYSTEM
  const processUserInput = useCallback(async (userInput: string, confidence: number = 0.8) => {
    if (!isVoiceAgentEnabled || isConversationLocked) return;
    
    // IMMEDIATE ACKNOWLEDGMENT - Respond instantly
    const immediateResponse = getImmediateAcknowledgment(userInput);
    await speakResponseWithOpenAI(immediateResponse);
    
    // Lock conversation to prevent multiple simultaneous responses
    setIsConversationLocked(true);
    setStatus('processing');
    setVoiceState(prev => ({ ...prev, isProcessing: true }));
    
    try {
      // Check if this is a portfolio query
      const isPortfolioQuery = userInput.toLowerCase().includes('portfolio') || 
                              userInput.toLowerCase().includes('value') || 
                              userInput.toLowerCase().includes('worth') || 
                              userInput.toLowerCase().includes('balance') || 
                              userInput.toLowerCase().includes('cash') || 
                              userInput.toLowerCase().includes('performance') || 
                              userInput.toLowerCase().includes('return') || 
                              userInput.toLowerCase().includes('allocation') || 
                              userInput.toLowerCase().includes('holdings') || 
                              userInput.toLowerCase().includes('market');
      
      let response: string;
      
      if (isPortfolioQuery) {
        // Query the backend API for portfolio data
        try {
          const apiResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/v1/avatar/avatar/query`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              query: userInput,
              session_id: `session_${Date.now()}`,
              user_id: 'default-user' // This would be the actual user ID in production
            })
          });
          
          if (apiResponse.ok) {
            const data = await apiResponse.json();
            response = data.response;
            
            // Log conversation to backend
            await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/v1/avatar/avatar/conversation/log`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                user_message: userInput,
                avatar_response: response,
                session_id: `session_${Date.now()}`,
                intent: 'PORTFOLIO_QUERY',
                confidence: confidence
              })
            });
          } else {
            // Fallback to local processing
            const intentAnalysis = analyzeIntentWithToM(userInput, confidence);
            const emotion = analyzeEmotion(userInput);
            const updatedContext = updateConversationContext(userInput, intentAnalysis, emotion);
            response = await generateContextualResponse(userInput, updatedContext);
          }
        } catch (apiError) {
          console.error('API call failed, using fallback:', apiError);
          // Fallback to local processing
          const intentAnalysis = analyzeIntentWithToM(userInput, confidence);
          const emotion = analyzeEmotion(userInput);
          const updatedContext = updateConversationContext(userInput, intentAnalysis, emotion);
          response = await generateContextualResponse(userInput, updatedContext);
        }
      } else {
        // Use local processing for non-portfolio queries
        const intentAnalysis = analyzeIntentWithToM(userInput, confidence);
        const emotion = analyzeEmotion(userInput);
        const updatedContext = updateConversationContext(userInput, intentAnalysis, emotion);
        response = await generateContextualResponse(userInput, updatedContext);
        
        // Update conversation context for local processing
        setConversationContext(updatedContext);
      }
      
      // Update conversation history
      const newHistory = {
        id: Date.now(),
        command: userInput,
        response: response,
        timestamp: new Date().toLocaleTimeString()
      };
      
      setCommandHistory(prev => [...prev, newHistory]);
      
      // Speak response with OpenAI TTS
      await speakResponseWithOpenAI(response);
      
      onResult?.(response);
      
    } catch (error) {
      console.error('Error processing user input:', error);
      const errorResponse = "I'm sorry, I encountered an error. Let me try to help you in a different way.";
      await speakResponseWithOpenAI(errorResponse);
      onError?.(error);
    } finally {
      setVoiceState(prev => ({ ...prev, isProcessing: false }));
      setStatus('ready');
      // Unlock conversation after a delay to allow for natural pauses
      setTimeout(() => {
        setIsConversationLocked(false);
      }, 2000);
    }
  }, [isVoiceAgentEnabled, isConversationLocked, onResult, onError]);

  // Enhanced intent analysis with Theory of Mind
  const analyzeIntentWithToM = (_input: string, confidence: number): any => {
    const lowerInput = _input.toLowerCase();
    
    // Enhanced trading-related intents with context
    const tradingPatterns = {
      'trading_action': ['trade', 'buy', 'sell', 'execute', 'order'],
      'market_inquiry': ['market', 'price', 'stock', 'crypto', 'forex', 'chart'],
      'portfolio_management': ['portfolio', 'investment', 'holdings', 'balance'],
      'ai_analysis': ['ai', 'signal', 'analysis', 'prediction', 'trend'],
      'risk_management': ['risk', 'stop loss', 'hedge', 'protection'],
      'performance_tracking': ['performance', 'returns', 'profit', 'loss']
    };
    
    // General conversation patterns
    const generalPatterns = {
      'greeting': ['hello', 'hi', 'hey', 'good morning', 'good afternoon'],
      'help_request': ['help', 'what can you do', 'how to', 'guide'],
      'gratitude': ['thank', 'thanks', 'appreciate'],
      'clarification': ['what do you mean', 'can you explain', 'i don\'t understand'],
      'interruption': ['stop', 'wait', 'hold on', 'excuse me']
    };
    
    // Analyze intent with confidence scoring
    let bestIntent = 'general_inquiry';
    let bestScore = 0;
    
    // Check trading patterns
    for (const [intent, patterns] of Object.entries(tradingPatterns)) {
      const score = patterns.reduce((total, pattern) => {
        return total + (lowerInput.includes(pattern) ? 1 : 0);
      }, 0) * confidence;
      
      if (score > bestScore) {
        bestScore = score;
        bestIntent = intent;
      }
    }
    
    // Check general patterns
    for (const [intent, patterns] of Object.entries(generalPatterns)) {
      const score = patterns.reduce((total, pattern) => {
        return total + (lowerInput.includes(pattern) ? 1 : 0);
      }, 0) * confidence;
      
      if (score > bestScore) {
        bestScore = score;
        bestIntent = intent;
      }
    }
    
    return {
      intent: bestIntent,
      confidence: bestScore,
      urgency: analyzeUrgency(_input),
      complexity: analyzeComplexity(_input),
      emotion: analyzeEmotion(_input)
    };
  };

  const analyzeEmotion = (_input: string): string => {
    const lowerInput = _input.toLowerCase();
    
    if (lowerInput.includes('frustrated') || lowerInput.includes('angry') || lowerInput.includes('upset')) {
      return 'frustrated';
    }
    if (lowerInput.includes('excited') || lowerInput.includes('happy') || lowerInput.includes('great')) {
      return 'excited';
    }
    if (lowerInput.includes('worried') || lowerInput.includes('concerned') || lowerInput.includes('nervous')) {
      return 'worried';
    }
    if (lowerInput.includes('confused') || lowerInput.includes('unsure') || lowerInput.includes('don\'t understand')) {
      return 'confused';
    }
    
    return 'neutral';
  };

  const analyzeUrgency = (_input: string): number => {
    const urgentWords = ['now', 'immediately', 'urgent', 'quick', 'fast', 'asap'];
    const lowerInput = _input.toLowerCase();
    
    return urgentWords.reduce((score, word) => {
      return score + (lowerInput.includes(word) ? 1 : 0);
    }, 0);
  };

  const analyzeComplexity = (_input: string): number => {
    const words = _input.split(' ').length;
    const hasNumbers = /\d/.test(_input);
    const hasTechnicalTerms = /algorithm|strategy|portfolio|analysis|technical/.test(_input.toLowerCase());
    
    return words * 0.1 + (hasNumbers ? 0.3 : 0) + (hasTechnicalTerms ? 0.4 : 0);
  };

  // Enhanced context update with ToM
  const updateConversationContext = (_input: string, intentAnalysis: any, emotion: string): ConversationContext => {
    const currentContext = conversationContext;
    const now = Date.now();
    
    // Update user engagement based on interaction frequency and complexity
    const timeSinceLastInteraction = now - currentContext.lastInteractionTime;
    const engagementDelta = Math.min(0.1, 1 / (timeSinceLastInteraction / 1000));
    const newEngagement = Math.min(1, currentContext.userEngagement + engagementDelta);
    
    // Update session history with enhanced data
    const newHistory = [...currentContext.sessionHistory, {
      timestamp: new Date().toISOString(),
      userInput: _input,
      aiResponse: '', // Will be filled by AI response
      intent: intentAnalysis.intent,
      emotion: emotion,
      confidence: intentAnalysis.confidence
    }];
    
    // Keep only last 15 interactions for context
    const recentHistory = newHistory.slice(-15);
    
    return {
      ...currentContext,
      userIntent: intentAnalysis.intent,
      currentTopic: determineTopic(intentAnalysis.intent, currentContext.currentTopic),
      previousCommands: [...currentContext.previousCommands, _input].slice(-10),
      sessionHistory: recentHistory,
      userEmotion: emotion,
      conversationFlow: 'processing',
      lastInteractionTime: now,
      userEngagement: newEngagement
    };
  };

  const determineTopic = (intent: string, currentTopic: string): string => {
    if (intent.includes('trading')) return 'trading';
    if (intent.includes('market')) return 'market_analysis';
    if (intent.includes('portfolio')) return 'portfolio';
    if (intent.includes('ai')) return 'ai_analysis';
    if (intent.includes('risk')) return 'risk_management';
    if (intent.includes('performance')) return 'performance_tracking';
    
    return currentTopic; // Maintain current topic if no clear transition
  };

  // Generate contextual response with ToM
  const generateContextualResponse = async (_input: string, _context: ConversationContext): Promise<string> => {
    const intent = _context.userIntent;
    const emotion = _context.userEmotion;
    // const engagement = _context.userEngagement;
    const urgency = analyzeUrgency(_input);
    
    // Adjust response style based on user emotion and engagement
    // let responseStyle = 'professional';
    // if (emotion === 'frustrated') responseStyle = 'empathetic';
    // if (emotion === 'excited') responseStyle = 'enthusiastic';
    // if (emotion === 'worried') responseStyle = 'reassuring';
    // if (emotion === 'confused') responseStyle = 'explanatory';
    
    // Generate response based on intent and context
    switch (intent) {
      case 'greeting':
        return generateGreetingResponse(_context);
      
      case 'trading_action':
        return generateTradingResponse(_input, _context, urgency);
      
      case 'market_inquiry':
        return generateMarketResponse(_input, _context);
      
      case 'portfolio_management':
        return generatePortfolioResponse(_input, _context);
      
      case 'ai_analysis':
        return generateAIAnalysisResponse(_input, _context);
      
      case 'risk_management':
        return generateRiskResponse(_input, _context);
      
      case 'performance_tracking':
        return generatePerformanceResponse(_input, _context);
      
      case 'help_request':
        return generateHelpResponse(_context);
      
      case 'gratitude':
        return generateGratitudeResponse(_context);
      
      case 'clarification':
        return generateClarificationResponse(_input, _context);
      
      case 'interruption':
        return generateInterruptionResponse(_context);
      
      default:
        return generateContextualFallbackResponse(_input, _context);
    }
  };

  const generateGreetingResponse = (_context: ConversationContext): string => {
    const timeOfDay = new Date().getHours();
    let greeting = '';
    
    if (timeOfDay < 12) greeting = 'Good morning';
    else if (timeOfDay < 17) greeting = 'Good afternoon';
    else greeting = 'Good evening';
    
    if (_context.sessionHistory.length === 0) {
      return `${greeting}! I'm your AI trading assistant. I can help you with market analysis, portfolio management, and trading strategies. What would you like to work on today?`;
    } else {
      return `${greeting}! Welcome back. I'm ready to help you with your trading needs. What can I assist you with?`;
    }
  };

  const generateTradingResponse = (_input: string, _context: ConversationContext, urgency: number): string => {
    const urgencyText = urgency > 0 ? 'I understand this is urgent. ' : '';
    
    if (_input.toLowerCase().includes('buy')) {
      return `${urgencyText}I can help you execute a buy order. Let me analyze current market conditions and identify the best entry points. What specific asset are you interested in trading?`;
    } else if (_input.toLowerCase().includes('sell')) {
      return `${urgencyText}I'll help you with the sell order. Let me check your current positions and market conditions to optimize your exit strategy. Which position would you like to close?`;
    } else {
      return `${urgencyText}I'm ready to help you with trading. I can analyze market conditions, identify opportunities, and execute trades. What specific action would you like to take?`;
    }
  };

  const generateMarketResponse = (_input: string, _context: ConversationContext): string => {
    return "I can provide real-time market data and analysis. I'm monitoring stocks, crypto, and forex markets with AI-powered insights. Which market would you like me to analyze for you?";
  };

  const generatePortfolioResponse = (_input: string, _context: ConversationContext): string => {
    return "I can help you optimize your portfolio with AI-driven insights. I can analyze your current holdings, suggest rebalancing strategies, and identify new investment opportunities. Would you like me to review your portfolio?";
  };

  const generateAIAnalysisResponse = (_input: string, _context: ConversationContext): string => {
    return "I use advanced AI algorithms to analyze market patterns, identify trends, and generate trading signals. I can provide technical analysis, sentiment analysis, and predictive insights. What type of analysis would you like?";
  };

  const generateRiskResponse = (_input: string, _context: ConversationContext): string => {
    return "I can help you manage risk with stop-loss orders, position sizing, and portfolio diversification strategies. Let me analyze your current risk exposure and suggest protective measures.";
  };

  const generatePerformanceResponse = (_input: string, _context: ConversationContext): string => {
    return "I can track your trading performance, calculate returns, and provide detailed analytics. Let me show you your current performance metrics and identify areas for improvement.";
  };

  const generateHelpResponse = (_context: ConversationContext): string => {
    return "I'm here to help! I can assist with market analysis, portfolio management, trading strategies, risk management, and AI-powered insights. Just tell me what you need, and I'll guide you through it.";
  };

  const generateGratitudeResponse = (_context: ConversationContext): string => {
    return "You're welcome! I'm here to help you succeed in your trading journey. Is there anything else you'd like to know or any other way I can assist you?";
  };

  const generateClarificationResponse = (_input: string, _context: ConversationContext): string => {
    return "I want to make sure I understand you correctly. Could you please rephrase that or provide more details about what you're looking for?";
  };

  const generateInterruptionResponse = (_context: ConversationContext): string => {
    return "I'll pause here. What would you like to discuss instead?";
  };

  const generateContextualFallbackResponse = (_input: string, _context: ConversationContext): string => {
    if (_context.sessionHistory.length > 1) {
      const lastInteraction = _context.sessionHistory[_context.sessionHistory.length - 2];
      return `I understand you're asking about ${_input}. Based on our conversation about ${lastInteraction.intent}, let me provide you with relevant information.`;
    }
    return `I heard you say "${_input}". I'm here to help with your trading and investment needs. Could you please be more specific about what you'd like to know?`;
  };

  // IMMEDIATE RESPONSE SYSTEM
  const getImmediateAcknowledgment = (_input: string): string => {
    const questionWords = ['what', 'how', 'when', 'where', 'why', 'who', 'which', 'can you', 'could you', 'would you'];
    const isQuestion = questionWords.some(word => _input.toLowerCase().includes(word));
    
    if (isQuestion) {
      return "I hear your question. Let me get that information for you right away.";
    }
    
    return "I understand. Let me help you with that.";
  };

  const generateProactiveResponse = (): string => {
    const responses = [
      "Is there anything specific about the markets you'd like to discuss?",
      "Would you like me to check your portfolio performance?",
      "I can analyze current market conditions if you're interested.",
      "Let me know if you need help with any trading decisions."
    ];
    return responses[Math.floor(Math.random() * responses.length)];
  };

  // OpenAI TTS Integration for natural voice
  const speakResponseWithOpenAI = async (text: string): Promise<void> => {
    if (!isVoiceAgentEnabled) return;
    
    setStatus('speaking');
    setVoiceState(prev => ({ ...prev, isSpeaking: true }));
    
    try {
      // Use OpenAI TTS API for natural voice
      const response = await fetch('/api/tts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: text,
          voice: 'alloy', // OpenAI voice options: alloy, echo, fable, onyx, nova, shimmer
          model: 'tts-1', // or 'tts-1-hd' for higher quality
          speed: 1.0
        }),
      });

      if (response.ok) {
        const audioBlob = await response.blob();
        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioUrl);
        
        audio.onended = () => {
          setStatus('ready');
          setVoiceState(prev => ({ ...prev, isSpeaking: false }));
          URL.revokeObjectURL(audioUrl);
        };
        
        audio.onerror = () => {
          console.error('Audio playback error');
          setStatus('ready');
          setVoiceState(prev => ({ ...prev, isSpeaking: false }));
          URL.revokeObjectURL(audioUrl);
        };
        
        await audio.play();
      } else {
        // Fallback to browser TTS if OpenAI fails
        console.warn('OpenAI TTS failed, falling back to browser TTS');
        await speakResponseWithTiming(text);
      }
    } catch (error) {
      console.error('OpenAI TTS error:', error);
      // Fallback to browser TTS
      await speakResponseWithTiming(text);
    }
  };

  // Enhanced speech synthesis with natural timing (fallback)
  const speakResponseWithTiming = async (text: string): Promise<void> => {
    if (!synthesisRef.current || !isVoiceAgentEnabled) return;
    
    setStatus('speaking');
    setVoiceState(prev => ({ ...prev, isSpeaking: true }));
    
    return new Promise((resolve) => {
      const utterance = new SpeechSynthesisUtterance(text);
      
      // Enhanced voice settings for natural speech
      utterance.rate = 0.9;
      utterance.pitch = 1.0;
      utterance.volume = 0.8;
      
      // Add natural pauses and emphasis
      const sentences = text.split('. ');
      let currentIndex = 0;
      
      const speakNextSentence = () => {
        if (currentIndex < sentences.length) {
          const sentence = sentences[currentIndex];
          utterance.text = sentence;
          
          utterance.onend = () => {
            currentIndex++;
            if (currentIndex < sentences.length) {
              // Natural pause between sentences
              setTimeout(speakNextSentence, 300);
            } else {
              setStatus('ready');
              setVoiceState(prev => ({ ...prev, isSpeaking: false }));
              resolve();
            }
          };
          
          synthesisRef.current?.speak(utterance);
        } else {
          setStatus('ready');
          setVoiceState(prev => ({ ...prev, isSpeaking: false }));
          resolve();
        }
      };
      
      speakNextSentence();
    });
  };

  // Toggle continuous listening
  const toggleContinuousListening = useCallback(() => {
    if (!isVoiceAgentEnabled) return;
    
    const newContinuousListening = !voiceState.isContinuousListening;
    setVoiceState(prev => ({ ...prev, isContinuousListening: newContinuousListening }));
    
    if (newContinuousListening) {
      startContinuousListening();
    } else {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    }
  }, [isVoiceAgentEnabled, voiceState.isContinuousListening]);

  // Drag functionality removed - avatar is locked in bottom right corner

  const cleanupVoiceSystem = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
    }
    if (conversationTimeoutRef.current) {
      clearInterval(conversationTimeoutRef.current);
    }
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
    }
  };

  const getStatusColor = () => {
    if (!isVoiceAgentEnabled) return 'bg-brand-plum/20';
    switch (status) {
      case 'ready': return 'bg-brand-teal/20';
      case 'listening': return 'bg-stone-800';
      case 'processing': return 'bg-brand-teal/20';
      case 'speaking': return 'bg-stone-800';
      case 'thinking': return 'bg-orange-500';
      case 'error': return 'bg-brand-plum/20';
      case 'permission-denied': return 'bg-brand-plum/20';
      case 'no-microphone': return 'bg-brand-plum/20';
      case 'not-supported': return 'bg-brand-plum/20';
      default: return 'bg-gray-500';
    }
  };

  const getStatusText = () => {
    if (!isVoiceAgentEnabled) return 'Voice Disabled';
    switch (status) {
      case 'ready': return 'Ready';
      case 'listening': return 'Listening...';
      case 'processing': return 'Processing...';
      case 'speaking': return 'Speaking...';
      case 'thinking': return 'Thinking...';
      case 'error': return 'Error';
      case 'permission-denied': return 'Permission Denied';
      case 'no-microphone': return 'No Microphone';
      case 'not-supported': return 'Not Supported';
      default: return 'Ready';
    }
  };

  if (!isVisible) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <button
          onClick={() => setIsVisible(true)}
          className="bg-gray-800 hover:bg-gray-700 text-white p-3 rounded-full shadow-premium transition-all duration-500"
          title="Show Avatar"
        >
          <Eye />
        </button>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="fixed bottom-4 right-4 z-50"
    >
      <div className={`bg-gray-800 border border-gray-600 rounded-xl shadow-premium transition-all duration-300 ${
        isMinimized 
          ? 'w-80 h-16' // Collapsed: compact header only
          : 'w-96 h-96' // Expanded: chat window size
      }`}>
        {/* Header */}
        <div 
          className="flex items-center justify-between p-3 border-b border-gray-600"
        >
          <div className="flex items-center space-x-2">
            <div className={`w-3 h-3 rounded-full ${getStatusColor()} animate-pulse`}></div>
            <span className="text-sm text-gray-300">{getStatusText()}</span>
            {voiceState.audioLevel > 0 && (
              <div className="flex space-x-1">
                {[...Array(Math.floor(voiceState.audioLevel * 5))].map((_, i) => (
                  <div key={i} className="w-1 h-1 bg-stone-700 rounded-full animate-pulse"></div>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center space-x-1">
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="text-slate-600 dark:text-slate-600 dark:text-slate-600 dark:text-slate-400 hover:text-white p-1 rounded transition-colors"
              title={isMinimized ? "Expand to Chat" : "Minimize"}
            >
              {isMinimized ? <Chat /> : <Minimize />}
            </button>
            <button
              onClick={() => setIsVisible(false)}
              className="text-slate-600 dark:text-slate-600 dark:text-slate-600 dark:text-slate-400 hover:text-white p-1 rounded transition-colors"
              title="Hide"
            >
              <EyeOff />
            </button>
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="text-slate-600 dark:text-slate-600 dark:text-slate-600 dark:text-slate-400 hover:text-white p-1 rounded transition-colors"
              title="Settings"
            >
              <Settings />
            </button>
            {/* Voice Agent Toggle Switch */}
            <button
              onClick={() => {
                console.log('🎛️ Toggle button clicked!');
                console.log('Current voice agent state:', isVoiceAgentEnabled);
                const nextState = !isVoiceAgentEnabled;
                toggleVoiceAgent();
                if (nextState) setIsSessionActivated(true);
                console.log('toggleVoiceAgent() called');
              }}
              className={`
                relative inline-flex items-center justify-center
                w-12 h-6 rounded-full transition-all duration-500 ease-in-out
                focus:outline-none focus:ring-2 focus:ring-brand-teal/50
                border border-white/10
                ${isVoiceAgentEnabled 
                  ? 'bg-brand-teal/20' 
                  : 'bg-brand-plum/20'
                }
              `}
              title={`${isVoiceAgentEnabled ? 'Disable' : 'Enable'} voice agent`}
            >
              {/* Toggle thumb */}
              <span
                className={`
                  w-5 h-5 bg-white rounded-full shadow-glow-sm transform transition-transform duration-500 ease-in-out
                  flex items-center justify-center
                  ${isVoiceAgentEnabled ? 'translate-x-2.5' : '-translate-x-2.5'}
                `}
              >
                {isVoiceAgentEnabled ? (
                  <span className="text-brand-teal text-[10px]">🎤</span>
                ) : (
                  <span className="text-brand-plum text-[10px]">🔇</span>
                )}
              </span>
            </button>
          </div>
        </div>

        {/* Chat Content - Only visible when expanded */}
        {!isMinimized && (
          <div className="flex-1 flex flex-col p-3">
            {/* Chat Messages Area */}
            <div className="flex-1 overflow-y-auto mb-3 space-y-3 custom-scrollbar px-1">
              {commandHistory.length > 0 ? (
                commandHistory.slice(-5).map((item) => (
                  <div key={item.id} className="space-y-2">
                    <div className="bg-brand-teal/10 border border-brand-teal/20 text-text-primary px-3 py-2 rounded-2xl rounded-tr-sm text-[11px] font-medium max-w-[85%] ml-auto shadow-glow-sm">
                      {item.command}
                    </div>
                    <div className="bg-bg-secondary/50 border border-white/5 text-text-secondary px-3 py-2 rounded-2xl rounded-tl-sm text-[11px] max-w-[85%]">
                      <div className="text-[9px] font-bold uppercase tracking-widest text-text-tertiary mb-1">Response</div>
                      {item.response}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-text-tertiary text-[10px] font-bold uppercase tracking-widest text-center py-8 opacity-50">
                  Neural Stream Inactive
                </div>
              )}
            </div>

            {/* Voice Disabled Message */}
            {!isVoiceAgentEnabled && (
              <div className="mb-4 p-3 bg-brand-plum/10 border border-brand-plum/30 rounded-2xl text-center">
                <div className="text-brand-plum-light text-[10px] font-black uppercase tracking-widest mb-1">Voice Inert</div>
                <div className="text-text-tertiary text-[9px] font-medium leading-relaxed">System offline. Activate toggle to initialize neural intercept.</div>
              </div>
            )}

            {/* Voice Status Display */}
            <div className="flex justify-center mb-4">
              <div className="text-center group">
                <div className={`w-14 h-14 rounded-full ${getStatusColor()} flex items-center justify-center mb-2 shadow-glow transition-all duration-300 group-hover:scale-105 border border-white/10`}>
                  {!isVoiceAgentEnabled ? <MicOff /> : (voiceState.isContinuousListening ? <Mic /> : <MicOff />)}
                </div>
                <div className="text-[9px] font-black uppercase tracking-[0.15em] text-text-tertiary">
                  {!isVoiceAgentEnabled ? 'System Offline' : (voiceState.isContinuousListening ? 'Monitoring' : 'Standby')}
                </div>
              </div>
            </div>

            {/* Current Command Display */}
            {currentCommand && (
              <div className="mb-3 p-2 bg-gray-700 rounded text-sm">
                <div className="text-slate-600 dark:text-slate-600 dark:text-slate-600 dark:text-slate-400 text-xs mb-1">You said:</div>
                <div className="text-white">{currentCommand}</div>
              </div>
            )}

            {/* Chat Input Area */}
            <div className="flex items-center gap-2 mt-auto">
              <input
                type="text"
                placeholder="Neural Input..."
                className="flex-1 px-4 py-2.5 bg-bg-secondary/50 border border-white/5 text-text-primary rounded-2xl text-[11px] font-medium focus:outline-none focus:ring-2 focus:ring-brand-teal/30 focus:border-brand-teal/50 transition-all placeholder:text-text-tertiary/50"
                disabled={!isVoiceAgentEnabled}
              />
              <button
                className="w-10 h-10 flex items-center justify-center bg-brand-teal text-bg-primary rounded-2xl shadow-glow transition-all active:scale-95 disabled:opacity-30 disabled:grayscale"
                disabled={!isVoiceAgentEnabled}
              >
                <Chat />
              </button>
            </div>
          </div>
        )}

        {/* Settings Panel */}
        {!isMinimized && showSettings && (
              <div className="p-3 border-b border-gray-600 bg-gray-700">
                <h4 className="text-sm font-medium text-white mb-2">Avatar Settings</h4>
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 text-sm text-gray-300">
                    <input
                      type="checkbox"
                      checked={isVoiceAgentEnabled}
                      onChange={toggleVoiceAgent}
                      className="rounded"
                    />
                    <span>Enable Voice Agent</span>
                  </label>
                  <label className="flex items-center space-x-2 text-sm text-gray-300">
                    <input
                      type="checkbox"
                      checked={voiceState.isContinuousListening}
                      onChange={toggleContinuousListening}
                      disabled={!isVoiceAgentEnabled}
                      className={`rounded ${!isVoiceAgentEnabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    />
                    <span className={!isVoiceAgentEnabled ? 'text-slate-700 dark:text-slate-300' : ''}>Continuous Listening</span>
                  </label>
                  <label className="flex items-center space-x-2 text-sm text-gray-300">
                    <input
                      type="checkbox"
                      checked={isVisible}
                      onChange={(e) => setIsVisible(e.target.checked)}
                      className="rounded"
                    />
                    <span>Show Avatar</span>
                  </label>
                  <div className="text-xs text-slate-600 dark:text-slate-600 dark:text-slate-600 dark:text-slate-400 mt-2 space-y-1">
                    <div>Current Topic: {conversationContext.currentTopic}</div>
                    <div>Intent: {conversationContext.userIntent}</div>
                    <div>Emotion: {conversationContext.userEmotion}</div>
                    <div>Engagement: {Math.round(conversationContext.userEngagement * 100)}%</div>
                    <div>Confidence: {Math.round(voiceState.confidence * 100)}%</div>
                    <div>Audio Level: {Math.round(voiceState.audioLevel * 100)}%</div>
                  </div>
                </div>
              </div>
            )}

        {/* Main Content - Only visible when collapsed */}
        {isMinimized && (
            <div className="p-3">
              {/* Voice Disabled Message */}
              {!isVoiceAgentEnabled && (
                <div className="mb-3 p-2 bg-brand-plum/40/20 border border-red-500/30 rounded text-center">
                  <div className="text-brand-plum text-sm font-medium mb-1">Voice Agent Disabled</div>
                  <div className="text-red-300 text-xs">Toggle the switch above to enable voice commands</div>
                </div>
              )}

              {/* Status Display */}
              <div className="flex justify-center mb-3">
                <div className="text-center">
                  <div 
                    className={`w-16 h-16 rounded-full ${getStatusColor()} flex items-center justify-center mb-2 cursor-pointer hover:scale-105 transition-transform`}
                    onClick={() => {
                      if (isVoiceAgentEnabled) setIsSessionActivated(true);
                      else {
                        toggleVoiceAgent();
                        setIsSessionActivated(true);
                      }
                    }}
                  >
                    {!isVoiceAgentEnabled ? <MicOff /> : (voiceState.isContinuousListening ? <Mic /> : <MicOff />)}
                  </div>
                  <div className="text-xs text-slate-600 dark:text-slate-600 dark:text-slate-600 dark:text-slate-400">
                    {!isVoiceAgentEnabled ? 'Voice Disabled' : (voiceState.isContinuousListening ? 'Always Listening' : 'Click to Listen')}
                  </div>
                </div>
              </div>

              {/* Current Command */}
              {currentCommand && (
                <div className="mb-3 p-2 bg-gray-700 rounded text-sm">
                  <div className="text-slate-600 dark:text-slate-600 dark:text-slate-600 dark:text-slate-400 text-xs mb-1">You said:</div>
                  <div className="text-white">{currentCommand}</div>
                </div>
              )}

              {/* Command History */}
              {commandHistory.length > 0 && (
                <div className="max-h-32 overflow-y-auto">
                  <div className="text-xs text-slate-600 dark:text-slate-600 dark:text-slate-600 dark:text-slate-400 mb-1">Recent Conversation:</div>
                  {commandHistory.slice(-3).map((item) => (
                    <div key={item.id} className="mb-2 p-2 bg-gray-700 rounded text-xs">
                      <div className="text-slate-600 dark:text-slate-600 dark:text-slate-600 dark:text-slate-400 mb-1">{item.timestamp}</div>
                      <div className="text-white mb-1">You: {item.command}</div>
                      <div className="text-gray-300">AI: {item.response}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
        )}
      </div>
    </div>
  );
};

export default FloatingAvatar; 